import { recordScope, type OrgTeamWorkbenchScope, type TeamProposal } from '@aioe/shared';
import { dateTime } from '@aioe/shared/wording/dateTime';
import type { TeamAttentionRow } from '@aioe/shared';
import { Briefcase, ClockArrowRotateLeft, CircleExclamation, Folder, Person, Persons } from '@gravity-ui/icons';
import { AlertDialog } from '@heroui/react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { routes, type TeamsWhere, type TeamCloudHolder, type TeamHistoryVersion, type TeamLocalRow, type TeamMachine, type TeamWhereRow } from '../api/client.ts';
import { ScalingList } from '../components/projectCode.tsx';
import { MemberFace } from '../components/teams/MemberFace.tsx';
import { NeedsAttention, TeamCheckResults } from '../components/teams/TeamCheck.tsx';
import { useMemberLookups } from '../components/teams/TeamsPanel.tsx';
import { TeamEditor } from '../components/teams/TeamEditor.tsx';
import { TeamScreen } from '../components/teams/TeamScreen.tsx';
import { editable } from '../components/teams/teamsModel.ts';
import { Button, ConfirmDialog, Empty, ErrorNote, inputClass, ListGroup, ListRow, Page, PageHeader, Stat, StatusMark } from '../components/ui.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { FoundryPublishLine } from '../components/FoundryPublish.tsx';
import { SpecialistEvaluation } from '../components/SpecialistEvaluation.tsx';
import { timeAgo, useLoad } from '../hooks.ts';
import { listCount, NoMatch, ShowMore, useListSearch, usePagedList } from '../pagedList.tsx';
import { SearchBox } from '../components/ui/SearchBox.tsx';
import { useAuth } from '../auth/AuthProvider.tsx';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "your jp-laptop", "Con's con-laptop", a node by its name. */
function machineLabel(m: TeamMachine, me?: string): string {
  if (m.kind !== 'workbench' || !m.owner) return m.deviceName;
  return me && m.owner.id === me ? `your ${m.deviceName}` : `${m.owner.name}'s ${m.deviceName}`;
}

/** The machine, its state, and when it holds an older version than the platform's, which one. */
function MachineLink({ m, me, version }: { m: TeamMachine; me?: string; version?: number }) {
  const behind = version !== undefined && m.version !== undefined && m.version < version;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className="status-dot" data-state={m.online ? 'online' : 'offline'} aria-hidden="true" />
      <Link to={m.kind === 'node' ? `/nodes?node=${encodeURIComponent(m.deviceId)}` : `/workbenches/${encodeURIComponent(m.deviceId)}`} className="font-medium text-accent hover:underline">
        {machineLabel(m, me)}
      </Link>
      {m.online ? null : <span className="text-xs text-muted">offline</span>}
      {behind ? <span className="text-xs text-muted">· version {m.version}, behind</span> : null}
    </span>
  );
}

/** "Refresh now" for a machine shown behind (teams-one-owner): a `teams.refresh` device order, issued by hand by an administrator, never automatically. */
function RefreshNow({ m, teamId }: { m: TeamMachine; teamId: string }) {
  const [state, setState] = useState<'idle' | 'asking' | 'asked' | 'failed'>('idle');
  const [error, setError] = useState<string>();
  if (state === 'asked') return <span className="text-xs text-muted">asked; it refreshes at its next heartbeat</span>;
  if (state === 'failed') return <span className="text-xs text-danger" title={error}>could not ask: {error}</span>;
  return (
    <Button
      size="sm"
      variant="tertiary"
      isPending={state === 'asking'}
      onPress={async () => {
        setState('asking');
        try {
          await routes.orderDevice(m.deviceId, 'teams.refresh', { teamIds: [teamId] });
          setState('asked');
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
          setState('failed');
        }
      }}
    >
      Refresh now
    </Button>
  );
}

function Machines({ machines, behind, me, version, teamId, mayRefresh }: { machines: TeamMachine[]; behind: number; me?: string; version?: number; teamId: string; mayRefresh?: boolean }) {
  if (!machines.length) return <p className="text-xs text-muted">Runs on no machine yet.</p>;
  const isBehind = (m: TeamMachine) => version !== undefined && m.version !== undefined && m.version < version;
  return (
    <div className="grid gap-1">
      <p className="text-xs text-muted tabular-nums">
        Runs on {plural(machines.length, 'machine', 'machines')}
        {behind ? `, ${behind} behind` : ''}
      </p>
      <ScalingList
        items={machines}
        noun="machines"
        keyOf={(m) => m.deviceId}
        text={(m) => `${machineLabel(m, me)} ${m.projectName ?? ''}`}
        render={(m) => (
          <span className="inline-flex flex-wrap items-center gap-2">
            <MachineLink m={m} me={me} version={version} />
            {mayRefresh && isBehind(m) && m.online ? <RefreshNow m={m} teamId={teamId} /> : null}
          </span>
        )}
        className="flex flex-wrap gap-x-4 gap-y-1 text-sm"
      />
    </div>
  );
}

/** A value in a proposed change, short: a string in quotes, nothing as "nothing", the rest as JSON cut to a line. */
function valueWords(v: unknown): string {
  if (v === undefined || v === null) return 'nothing';
  if (typeof v === 'string') return `“${v.length > 60 ? `${v.slice(0, 57)}…` : v}”`;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  const json = JSON.stringify(v);
  return json.length > 80 ? `${json.slice(0, 77)}…` : json;
}

/** The changes waiting for the team's owner (teams-one-owner), field by field, with Accept and Decline for those who decide. */
function Proposals({ t, onChanged }: { t: TeamWhereRow; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const list = useLoad(() => (open ? routes.teamProposals(t.id, 'pending') : Promise.resolve(undefined)), [open, t.id, t.pendingProposals]);
  const [declining, setDeclining] = useState<string>();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const n = t.pendingProposals ?? 0;
  if (!n && !open) return null;
  const decide = async (p: TeamProposal, decision: 'accept' | 'decline') => {
    setError(undefined);
    try {
      await routes.decideTeamProposal(t.id, p.id, decision, decision === 'decline' ? reason.trim() || undefined : undefined);
      setDeclining(undefined);
      setReason('');
      list.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <div className="grid gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <StatusMark state="medium" label={`${plural(n, 'change', 'changes')} waiting for the owner`} />
        <Button size="sm" variant="tertiary" onPress={() => setOpen((o) => !o)}>{open ? 'Hide' : 'Show'}</Button>
      </div>
      {open ? (
        <div className="grid gap-2 rounded-lg bg-surface-secondary p-3">
          <ErrorNote error={error ?? list.error} />
          {list.data?.proposals.length ? (
            list.data.proposals.map((p) => (
              <div key={p.id} className="grid gap-1 text-sm">
                <p>
                  <span className="font-medium">{p.proposedByName ?? 'Someone'}</span> <span className="text-muted">proposed {timeAgo(p.proposedAt)}, against version {p.baseVersion}</span>
                </p>
                <ul className="grid gap-0.5 text-xs">
                  {p.changes.map((c) => (
                    <li key={c.path} className="break-words">
                      <span className="mono">{c.path}</span>: {valueWords(c.from)} <span className="text-muted">becomes</span> {valueWords(c.to)}
                    </li>
                  ))}
                </ul>
                {list.data?.mayDecide ? (
                  declining === p.id ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <input className={`${inputClass} max-w-sm`} aria-label="Why it is declined" placeholder="Why, in a sentence (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
                      <Button size="sm" variant="danger" onPress={() => decide(p, 'decline')}>Decline</Button>
                      <Button size="sm" variant="tertiary" onPress={() => setDeclining(undefined)}>Back</Button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button size="sm" variant="primary" onPress={() => decide(p, 'accept')}>Accept</Button>
                      <Button size="sm" variant="tertiary" onPress={() => setDeclining(p.id)}>Decline…</Button>
                    </div>
                  )
                ) : null}
              </div>
            ))
          ) : list.data ? (
            <p className="text-sm text-muted">Nothing waits on the owner.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Hand a team to another person: search the organisation's people, pick one. */
function OwnerDialog({ team, onClose, onChanged }: { team: TeamWhereRow; onClose: () => void; onChanged: () => void }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [results, setResults] = useState<Array<{ id: string; name: string; email?: string }>>([]);
  const searching = q.trim().length >= 2;
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) return;
    let cancelled = false;
    const t = setTimeout(() => {
      routes
        .peopleSearch(term)
        .then((r) => !cancelled && setResults(r.people.map((p) => ({ id: p.id, name: p.name, ...(p.email ? { email: p.email } : {}) }))))
        .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);
  const pick = async (userId: string) => {
    setBusy(true);
    setError(undefined);
    try {
      await routes.setTeamOwner(team.id, userId);
      onChanged();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog.Backdrop isOpen onOpenChange={(open) => !open && !busy && onClose()}>
      <AlertDialog.Container>
        <AlertDialog.Dialog className="sm:max-w-md">
          <AlertDialog.Header>
            <AlertDialog.Icon status="accent" />
            <AlertDialog.Heading>Who owns {team.name}?</AlertDialog.Heading>
          </AlertDialog.Header>
          <AlertDialog.Body>
            <div className="grid gap-3 text-sm">
              <p className="text-muted">{team.owner ? `${team.owner.name ?? team.owner.id} owns it now. ` : 'It has no owner yet. '}The owner decides proposed changes and sponsors its agent identities.</p>
              <input className={inputClass} aria-label="Find a person" placeholder="Name or email, at least two characters" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
              <ErrorNote error={error} />
              {searching && results.length ? (
                <ul className="divide-y divide-separator">
                  {results.map((p) => (
                    <li key={p.id}>
                      <button type="button" className="flex w-full items-baseline justify-between gap-3 py-1.5 text-left hover:text-accent disabled:opacity-60" disabled={busy || p.id === team.owner?.id} onClick={() => pick(p.id)}>
                        <span className="font-medium">{p.name}</span>
                        <span className="truncate text-xs text-muted">{p.id === team.owner?.id ? 'owns it now' : p.email ?? ''}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : searching ? (
                <p className="text-xs text-muted">Nobody by that name yet.</p>
              ) : null}
            </div>
          </AlertDialog.Body>
          <AlertDialog.Footer>
            <Button variant="tertiary" isDisabled={busy} onPress={onClose}>Cancel</Button>
          </AlertDialog.Footer>
        </AlertDialog.Dialog>
      </AlertDialog.Container>
    </AlertDialog.Backdrop>
  );
}

/** Foundry members (D273): held by a Foundry project, from the member record, with the machine that drives each. */
function FoundryHolders({ teamId, holders, me, onChanged }: { teamId: string; holders?: TeamCloudHolder[]; me?: string; onChanged: () => void }) {
  if (!holders?.length) return null;
  return (
    <ul className="grid gap-1">
      {holders.map((h) => {
        const words = h.driver?.kind === 'workbench' && me && h.driver.owner?.id === me ? h.words.replace(/, from .*$/, ', from your workbench') : h.words;
        if (h.foundry) return <FoundryPublishLine key={h.member} teamId={teamId} h={{ ...h, words }} onChanged={onChanged} />;
        const agent = h.agentName ? `${h.adopted ? 'adopted agent' : 'agent'} ${h.agentName}${h.agentVersion ? `, version ${h.agentVersion}` : ''}` : 'not published yet';
        return (
          <li key={h.member} className="text-sm">
            <span className="font-medium">{h.member}</span> <span className="text-muted">{words}</span>
            {h.driver && !h.driver.online ? (
              <span className="ml-1.5 inline-flex items-center gap-1 text-xs text-muted">
                <span className="status-dot" data-state="offline" aria-hidden="true" />
                {h.driver.kind === 'node' ? 'node offline' : 'workbench offline'}
              </span>
            ) : null}
            <p className="mono text-xs text-muted" title={h.projectEndpoint}>{`${h.deployment} · ${agent}`}</p>
          </li>
        );
      })}
    </ul>
  );
}

/** "Coder, Reviewer · 2 agents": the members in words, people by name. */
export function memberWords(members?: Array<{ name: string; kind: 'agent' | 'person' }>): string {
  if (!members?.length) return '';
  const agents = members.filter((m) => m.kind !== 'person').length;
  return `${members.map((m) => m.name).join(', ')}${agents ? ` · ${plural(agents, 'agent', 'agents')}` : ''}`;
}

/** Where the workbench keeps a team (D286), in the console's section words; the workbench's own groups. */
const SCOPE_GROUPS: Array<{ id: OrgTeamWorkbenchScope; title: string }> = [
  { id: 'repository', title: 'In a repository' },
  { id: 'project', title: 'In a project' },
  { id: 'machine', title: 'On a person’s computer, every project' },
];

/** Its workbench scope, or what `origin.scope` implies for a record no workbench has placed yet (organisation → machine). */
const scopeOf = (t: TeamWhereRow): OrgTeamWorkbenchScope => recordScope({ workbenchScope: t.workbenchScope, origin: { scope: t.scope } }) ?? 'project';

function scopeWords(t: TeamWhereRow): string {
  return SCOPE_GROUPS.find((g) => g.id === scopeOf(t))!.title.toLowerCase();
}

/** A copy made before D286 (a machine-wide definition used in a second project): an id ending in `-` and six hex digits. */
const COPY_ID = /^(.+)-[0-9a-f]{6}$/;

/** The team this one looks like a copy of: same kind and name, preferably the id it was copied from. Never a copy itself. */
function originalOf(t: TeamWhereRow, all: TeamWhereRow[]): TeamWhereRow | undefined {
  const m = COPY_ID.exec(t.id);
  if (!m) return undefined;
  const twins = all.filter((o) => o.id !== t.id && (o.kind ?? 'team') === (t.kind ?? 'team') && o.name === t.name && !COPY_ID.test(o.id));
  return twins.find((o) => o.id === m[1]) ?? twins[0];
}

/** The projects that use it: those it is granted to, with the one it was defined in, by name. */
function usedBy(t: TeamWhereRow): string[] {
  const names = new Map<string, string | undefined>();
  if (t.origin) names.set(t.origin.projectId, t.origin.projectName);
  for (const g of t.grants) names.set(g.id, g.name ?? names.get(g.id));
  return [...names.values()].map((n) => n ?? 'a project');
}

/** The members' faces: each one's character and colour, or the person's picture (MemberFace). */
function Faces({ faces }: { faces?: TeamWhereRow['faces'] }) {
  if (!faces?.length) return null;
  const shown = faces.slice(0, 6);
  return (
    <span className="flex shrink-0 items-center gap-1" aria-label={`Members: ${faces.map((f) => f.name).join(', ')}`}>
      {shown.map((f) => (
        <span key={f.name} title={f.name} className="inline-flex">
          <MemberFace member={f} />
        </span>
      ))}
      {faces.length > shown.length ? <span className="ps-1 text-xs text-muted tabular-nums">+{faces.length - shown.length}</span> : null}
    </span>
  );
}

/** What an audit action did to the team, in a few words. */
function historyAction(v: TeamHistoryVersion): string {
  switch (v.action) {
    case 'team.create':
      return 'Created';
    case 'team.update':
      return v.proposal ? 'Changed (an accepted proposal)' : 'Changed';
    case 'team.used':
      return `Used by ${v.project ?? 'a project'}`;
    case 'team.unused':
      return `No longer used by ${v.project ?? 'a project'}`;
    case 'team.merged':
      return 'A copy merged into it';
    case 'team.owner.set':
      return 'Owner changed';
    case 'team.proposal.create':
      return 'A change proposed';
    case 'team.proposal.accept':
      return 'A proposal accepted';
    case 'team.proposal.decline':
      return 'A proposal declined';
    case 'team.delete':
      return 'Deleted';
    case 'team.repair':
      return v.outcome === 'denied' ? 'A fix refused' : 'Repaired';
    default:
      return v.action.replace(/^team\./, '').replace(/\./g, ' ');
  }
}

/** Every version of the team from the audit trail (21 Oct 2026): when, who, from which project's route, on which machine, which fields. */
function History({ t, me, extra }: { t: TeamWhereRow; me?: string; extra?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const hist = useLoad(() => (open ? routes.teamHistory(t.id) : Promise.resolve(undefined)), [open, t.id, t.version]);
  return (
    <div className="grid gap-1.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted tabular-nums">
        <span>
          Version {t.version}
          {t.lastChange ? `, changed ${timeAgo(t.lastChange.at)} by ${me && t.lastChange.by === me ? 'you' : t.lastChange.byName ?? t.lastChange.by}` : ''}
          {t.lastChange?.paths.length ? ` (${t.lastChange.paths.slice(0, 3).join(', ')}${t.lastChange.paths.length > 3 ? ` and ${t.lastChange.paths.length - 3} more` : ''})` : ''}
        </span>
        <span className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="tertiary" onPress={() => setOpen((o) => !o)}>{open ? 'Hide history' : 'History'}</Button>
          {extra}
        </span>
      </div>
      {open ? (
        <div className="grid gap-2 rounded-lg bg-surface-secondary p-3 text-sm">
          <ErrorNote error={hist.error} />
          {hist.data ? (
            <>
              <ul className="grid gap-1.5">
                {hist.data.versions.map((v, i) => {
                  const who = v.by ? (me && v.by === me ? 'you' : v.byName ?? v.by) : 'the platform';
                  // "Used by X" already names the project; every other action says which project's route it came through.
                  const viaProject = v.action === 'team.used' || v.action === 'team.unused' ? undefined : v.projectName ? `through ${v.projectName}` : v.projectId ? `through ${v.projectId}` : undefined;
                  const where = [viaProject, v.deviceName ? `on ${v.deviceName}${v.deviceKind === 'node' ? ' (node)' : ''}` : undefined].filter(Boolean).join(', ');
                  return (
                    <li key={`${v.at}-${i}`} className="grid gap-0.5">
                      <p className="tabular-nums">
                        {v.version !== undefined ? <span className="font-medium">v{v.version}</span> : null} {historyAction(v)} <span className="text-muted">· {dateTime(v.at)} · {who}{where ? ` ${where}` : ''}</span>
                      </p>
                      {v.paths.length ? <p className="mono break-words text-xs text-muted">{v.paths.join(', ')}</p> : v.action === 'team.update' ? <p className="text-xs text-muted">The fields this version touched were changed again later; only the newest change per field is kept.</p> : null}
                      {v.refused?.length ? <p className="text-xs text-muted">Kept as the platform had them: {v.refused.join(', ')}</p> : null}
                    </li>
                  );
                })}
              </ul>
              {!hist.data.versions.length ? <p className="text-muted">No history recorded yet.</p> : null}
              <p className="text-xs text-muted">{hist.data.note}</p>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** The roster in words: "Coder, Backend Developer · Reviewer · Ana (person) · Hannah (specialist)". */
function Roster({ roster }: { roster?: TeamWhereRow['roster'] }) {
  if (!roster?.length) return <span>No members.</span>;
  return (
    <span className="block">
      {roster.map((m, i) => (
        <span key={m.name}>
          {i ? <span className="text-muted"> · </span> : null}
          <span className="text-foreground">{m.name}</span>
          {m.human ? <span className="text-muted"> (person)</span> : m.ref ? <span className="text-muted"> (specialist)</span> : m.role ? <span className="text-muted">, {m.role}</span> : null}
        </span>
      ))}
    </span>
  );
}

/** "5 of 5 with an identity", and identities kept for former members said apart, never counted as the team's. */
function IdentityMark({ t }: { t: TeamWhereRow }) {
  const leftover = t.identities.leftover ?? 0;
  if (t.identities.problems) return <StatusMark state="error" label={plural(t.identities.problems, 'identity problem', 'identity problems')} />;
  if (!t.members && !leftover) return null;
  const count = t.members ? `${t.identities.registered} of ${t.members} with an identity` : 'No agent members';
  if (leftover) return <StatusMark state="medium" label={`${count} · ${plural(leftover, 'left from a former member', 'left from former members')}`} />;
  return <StatusMark state={t.identities.registered >= t.members ? 'online' : 'idle'} label={count} />;
}

function TeamRow({ t, all, local, me, onChanged, onMerge, onOwner, mayRefresh, standalone, attention, checking, onCheck }: { t: TeamWhereRow; all: TeamWhereRow[]; local: TeamLocalRow[]; me?: string; onChanged: () => void; onMerge?: (copy: TeamWhereRow, into: TeamWhereRow) => void; onOwner?: (t: TeamWhereRow) => void; mayRefresh?: boolean; standalone?: boolean; attention?: TeamAttentionRow; checking: boolean; onCheck: (open: boolean) => void }) {
  // Opened from a link or the attention filter: the team comes into view with its results.
  useEffect(() => {
    if (checking) document.getElementById(`team-${t.id}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [checking, t.id]);
  const origin = t.origin?.definedByName ? `defined by ${t.origin.definedByName}${t.origin.projectName ? ` in ${t.origin.projectName}` : ''}` : t.origin?.projectName ? `defined in ${t.origin.projectName}` : '';
  // One owner per team (teams-one-owner): said once, with the hand-over for those who may.
  const owned = t.owner ? `Owned by ${me && t.owner.id === me ? 'you' : t.owner.name ?? t.owner.id}` : 'No owner yet; its next save names one';
  const users = usedBy(t);
  // The projects that use it, each a link; the team's definition project first.
  const projectLinks = (() => {
    const seen = new Map<string, string | undefined>();
    if (t.origin) seen.set(t.origin.projectId, t.origin.projectName);
    for (const g of t.grants) seen.set(g.id, g.name ?? seen.get(g.id));
    return [...seen.entries()];
  })();
  // How it was made (D272): the designer, or a draft from real work; the machine named by its holder when known.
  const madeOn = t.madeBy?.deviceId ? t.machines.find((m) => m.deviceId === t.madeBy!.deviceId) : undefined;
  const where = madeOn ? ` on ${madeOn.owner?.name ? `${madeOn.owner.name.split(/[\s@]/)[0]}'s ${madeOn.kind === 'node' ? 'node' : 'workbench'}` : madeOn.deviceName}` : '';
  const made = t.madeBy?.designed ? `Designed in a conversation${where}` : t.madeBy?.drafted ? `Drafted from ${t.madeBy.taskId ?? 'real work'}${where}` : undefined;
  // The team's context budget (D272 part 3), said only when the team chose one for members that choose none.
  const budget = t.contextProfile && t.contextProfile !== 'auto' ? `${t.contextProfile[0]!.toUpperCase()}${t.contextProfile.slice(1)} context budget` : undefined;
  const original = originalOf(t, all);
  // A copy and its original are said once, by the copy's own line, never again as "same name".
  const same = t.sameName.map((id) => all.find((o) => o.id === id)).filter((o): o is TeamWhereRow => !!o && o !== original && originalOf(o, all) !== t);
  const copies = local.filter((l) => l.name === t.name);
  const copyHosts = [...new Set(copies.map((c) => machineLabel(c.machine)))];
  const notes = [
    ...same.map((o) => `Same name as the ${o.kind === 'specialist' ? 'specialist' : 'team'} kept ${scopeWords(o)}${o.origin?.projectName ? ` (${o.origin.projectName})` : ''}`),
    ...(copies.length ? [`${plural(copies.length, 'local copy', 'local copies')} on ${copyHosts.join(', ')}`] : []),
  ];
  // The rows of the group it stands in (TeamView's "The team"): who is on it, who uses it, who owns it, where it runs,
  // its history and check, then what waits on the owner and what it looks like a copy of.
  return (
    <>
      <ListRow
        id={`team-${t.id}`}
        icon={t.kind === 'specialist' ? <Person /> : <Persons />}
        title={standalone ? t.name : <Link to={`/teams/${encodeURIComponent(t.id)}`} className="text-accent hover:underline">{t.name}</Link>}
        description={<Roster roster={t.roster} />}
        status={<IdentityMark t={t} />}
        compactControl
        control={<span className="hidden @[40rem]:inline-flex"><Faces faces={t.faces} /></span>}
      />
      <ListRow
        icon={<Folder />}
        title={users.length ? 'Used by' : 'Not used by a project yet'}
        description={
          projectLinks.length ? (
            <>
              {projectLinks.map(([id, name], i) => (
                <span key={id}>
                  {i ? ', ' : ''}
                  <Link to={`/projects/${encodeURIComponent(id)}`} className="text-accent hover:underline">
                    {name ?? id}
                  </Link>
                </span>
              ))}
            </>
          ) : undefined
        }
      />
      <ListRow
        icon={<Person />}
        title={owned}
        description={[origin, made, budget].filter(Boolean).join(' · ') || undefined}
        {...(onOwner
          ? {
              compactControl: true,
              control: (
                <Button size="sm" variant="ghost" onPress={() => onOwner(t)}>
                  {t.owner ? 'Change owner' : 'Set an owner'}
                </Button>
              ),
            }
          : {})}
      />
      <ListRow icon={<Briefcase />} title="Machines" description={t.machines.length ? undefined : 'Runs on no machine yet.'}>
        {t.machines.length ? <Machines machines={t.machines} behind={t.behind} me={me} version={t.version} teamId={t.id} mayRefresh={mayRefresh} /> : undefined}
      </ListRow>
      <ListRow
        icon={<ClockArrowRotateLeft />}
        title="History and check"
        description={`Every version from the audit trail, and a check of what the ${t.kind === 'specialist' ? 'specialist' : 'team'} needs to run.`}
        {...(attention
          ? {
              status: (
                <button type="button" className="shrink-0 cursor-[var(--cursor-interactive)] rounded-md hover:underline" onClick={() => onCheck(true)} aria-label={`${t.name} needs attention: show its check`}>
                  <NeedsAttention errors={attention.errors} warnings={attention.warnings} />
                </button>
              ),
            }
          : {})}
      >
        <div className="grid gap-2 text-sm">
          <History
            t={t}
            me={me}
            extra={
              checking ? null : (
                <Button size="sm" variant="tertiary" onPress={() => onCheck(true)}>
                  Check this {t.kind === 'specialist' ? 'specialist' : 'team'}
                </Button>
              )
            }
          />
          {checking ? <TeamCheckResults teamId={t.id} kind={t.kind} onClose={() => onCheck(false)} onRepaired={onChanged} /> : null}
        </div>
      </ListRow>
      <Proposals t={t} onChanged={onChanged} />
      <FoundryHolders teamId={t.id} holders={t.cloud} me={me} onChanged={onChanged} />
      <SpecialistEvaluation t={t} onChanged={onChanged} />
      {notes.length || original ? (
        <div className="grid gap-1.5 text-sm">
          {notes.map((n) => (
            <p key={n} className="m-0 text-xs text-muted">
              {n}
            </p>
          ))}
          {original ? (
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
              <StatusMark state="medium" label={`Looks like a copy of ${original.name}`} />
              {onMerge ? (
                <Button size="sm" variant="secondary" onPress={() => onMerge(t, original)}>
                  Merge into {original.name}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/** The editor for one team, with what it looks things up in; mounted only while someone edits. */
function TeamEditPart({ t, all, onDone, onCancel }: { t: TeamWhereRow; all: TeamWhereRow[]; onDone: () => void; onCancel: () => void }) {
  const kind = t.kind === 'specialist' ? 'specialist' : 'team';
  const loaded = useLoad(() => Promise.all([routes.teams(kind === 'specialist' ? 'specialist' : undefined), routes.portfolio()]), [t.id, kind]);
  const { lookups } = useMemberLookups(all.filter((x) => x.kind === 'specialist').map((x) => ({ id: x.id, name: x.name })));
  const doc = loaded.data?.[0].find((x) => x.id === t.id);
  const tree = loaded.data?.[1];
  if (loaded.error) return <ErrorNote error={loaded.error} />;
  if (!loaded.data) return <p className="text-sm text-muted">Opening the editor…</p>;
  if (!doc || !tree) return <p className="text-sm text-muted">This {kind} can no longer be edited here: it may have been removed.</p>;
  const projects = [...tree.areas.flatMap((a) => a.projects), ...tree.unfiled];
  const projectName = (id: string) => projects.find((p) => p.id === id)?.name ?? id;
  return <TeamEditor team={editable(doc)} kind={kind} where={{ kind: 'portfolio', tree }} lookups={lookups} projectName={projectName} onDone={onDone} onCancel={onCancel} />;
}

/**
 * One team on its own page (`/teams/:teamId`): what it is in numbers, then the team itself (roster, owner, the
 * projects that use it, machines, history and its check) and, for those who may, its editor.
 */
function TeamView({ t, all, local, me, tone, mayEdit, onChanged, onMerge, onOwner, mayRefresh, attention, checking, onCheck, editing, onEditing }: { t: TeamWhereRow; all: TeamWhereRow[]; local: TeamLocalRow[]; me?: string; tone: (typeof VIEW_TONES)[string]; mayEdit: boolean; onChanged: () => void; onMerge?: (copy: TeamWhereRow, into: TeamWhereRow) => void; onOwner?: (t: TeamWhereRow) => void; mayRefresh?: boolean; attention?: TeamAttentionRow; checking: boolean; onCheck: (open: boolean) => void; editing: boolean; onEditing: (on: boolean) => void }) {
  const word = t.kind === 'specialist' ? 'specialist' : 'team';
  const projects = new Set([...(t.origin ? [t.origin.projectId] : []), ...t.grants.map((g) => g.id)]).size;
  const people = (t.roster ?? []).filter((m) => m.human).length;
  return (
    <div className="grid gap-4">
      <div className="art-stats">
        <Stat tone={tone!} icon={<Persons />} label="Members" value={t.roster?.length ?? t.members} detail={people ? `${plural(t.members, 'agent', 'agents')}, ${plural(people, 'person', 'people')}` : plural(t.members, 'agent', 'agents')} />
        <Stat tone={tone!} icon={<Folder />} label="Projects" value={projects} detail={projects ? 'use it' : 'none use it yet'} />
        <Stat tone={tone!} icon={<Briefcase />} label="Machines" value={t.machines.length} detail={t.behind ? `${t.behind} behind` : t.machines.length ? 'run it' : 'none run it yet'} />
        <Stat tone={tone!} icon={<ClockArrowRotateLeft />} label="Version" value={t.version} detail={t.lastChange ? `changed ${timeAgo(t.lastChange.at)}` : ''} />
      </div>
      {editing ? (
        <TeamEditPart
          t={t}
          all={all}
          onDone={() => {
            onEditing(false);
            onChanged();
          }}
          onCancel={() => onEditing(false)}
        />
      ) : (
        <ListGroup
          title={`The ${word}`}
          description={`Kept ${scopeWords(t)}. Who is on it, who owns it, the projects that use it, the machines that run it and its history.`}
          actions={
            mayEdit ? (
              <Button size="sm" variant="secondary" onPress={() => onEditing(true)}>
                Edit
              </Button>
            ) : undefined
          }
        >
          <TeamRow t={t} all={all} local={local} me={me} onChanged={onChanged} onMerge={onMerge} onOwner={onOwner} mayRefresh={mayRefresh} attention={attention} standalone checking={checking} onCheck={onCheck} />
        </ListGroup>
      )}
    </div>
  );
}

/** For a person who is not a manager: the teams of their projects that need attention, each with its check. */
function YourTeamsNeedingAttention({ rows, open, onCheck, onChanged }: { rows: TeamAttentionRow[]; open: Set<string>; onCheck: (id: string, on: boolean) => void; onChanged: () => void }) {
  if (!rows.length) return null;
  return (
    <ListGroup title="Your teams that need attention" count={rows.length} description="Teams and specialists of your projects whose check found something. Check one to see what, and fix it.">
      {rows.map((a) => (
        <ListRow
          key={a.teamId}
          id={`team-${a.teamId}`}
          icon={a.kind === 'specialist' ? <Person /> : <Persons />}
          title={a.name}
          description={a.kind === 'specialist' ? 'Specialist' : 'Team'}
          status={<NeedsAttention errors={a.errors} warnings={a.warnings} />}
          {...(open.has(a.teamId)
            ? {}
            : {
                control: (
                  <Button size="sm" variant="secondary" onPress={() => onCheck(a.teamId, true)}>
                    Check this {a.kind === 'specialist' ? 'specialist' : 'team'}
                  </Button>
                ),
              })}
        >
          {open.has(a.teamId) ? <TeamCheckResults teamId={a.teamId} kind={a.kind} onClose={() => onCheck(a.teamId, false)} onRepaired={onChanged} /> : undefined}
        </ListRow>
      ))}
    </ListGroup>
  );
}

/** "Coder · Reviewer · Ana (person)": the roster in a line of words, for a row. */
const rosterWords = (roster?: TeamWhereRow['roster']) => (roster?.length ? roster.map((m) => `${m.name}${m.human ? ' (person)' : m.ref ? ' (specialist)' : ''}`).join(' · ') : 'No members');

/**
 * One team in the list: a row you press that opens the team's page, with who is on it, who uses it and owns it in
 * words and its state at the end. A team that needs attention opens on its Details with its check showing.
 */
function TeamListRow({ t, all, me, attention }: { t: TeamWhereRow; all: TeamWhereRow[]; me?: string; attention?: TeamAttentionRow }) {
  const users = usedBy(t);
  const owned = t.owner ? `owned by ${me && t.owner.id === me ? 'you' : (t.owner.name ?? t.owner.id)}` : 'no owner yet';
  const original = originalOf(t, all);
  const pending = t.pendingProposals ?? 0;
  const more = [
    users.length ? `Used by ${users.join(', ')}` : 'Not used by a project yet',
    owned,
    t.machines.length ? `runs on ${plural(t.machines.length, 'machine', 'machines')}${t.behind ? `, ${t.behind} behind` : ''}` : 'runs on no machine yet',
    pending ? `${plural(pending, 'change', 'changes')} waiting for the owner` : '',
    original ? `looks like a copy of ${original.name}` : '',
  ].filter(Boolean);
  const path = `/teams/${encodeURIComponent(t.id)}`;
  return (
    <ListRow
      id={`team-${t.id}`}
      to={attention ? `${path}?tab=details&check=${encodeURIComponent(t.id)}` : path}
      icon={t.kind === 'specialist' ? <Person /> : <Persons />}
      title={t.name}
      description={
        <>
          <span className="block truncate">{rosterWords(t.roster)}</span>
          <span className="block truncate">{more.join(' · ')}</span>
        </>
      }
      trailing={<span className="hidden @[40rem]:inline-flex">{attention ? null : <Faces faces={t.faces} />}</span>}
      status={attention ? <NeedsAttention errors={attention.errors} warnings={attention.warnings} /> : <IdentityMark t={t} />}
    />
  );
}

export function TeamsPage() {
  const tone = VIEW_TONES.teams!;
  const { account } = useAuth();
  const me = account?.id;
  // A 403 is a calm page ("portfolio managers see this"), told apart by status, never by the sentence's words.
  // The list is searched and paged on the server; one team's own page asks for that team (and the teams sharing its name) by id.
  const { teamId, member } = useParams();
  const search = useListSearch();
  const [rev, setRev] = useState(0);
  const list = usePagedList((cursor) => routes.teamsWherePage({ q: search.q, cursor }), { q: search.q, rev }, { keyOf: (t) => t.id });
  const oneAnswer = useLoad(() => (teamId ? routes.teamsWhereOne(teamId) : Promise.resolve(undefined)), [teamId, rev]);
  const where = { error: teamId ? oneAnswer.error : list.error, reload: () => { setRev((r) => r + 1); list.reload(); oneAnswer.reload(); } };
  const listExtra = list.extra;
  const forbidden = teamId ? oneAnswer.data?.forbidden === true : listExtra?.forbidden === true;
  const data: (TeamsWhere & { forbidden?: boolean }) | undefined = teamId ? oneAnswer.data : listExtra ? { teams: list.items, local: listExtra.local, ...(listExtra.mayMerge ? { mayMerge: true as const } : {}), ...(listExtra.mayRefresh ? { mayRefresh: true as const } : {}) } : undefined;
  const teams = data?.teams ?? [];
  const local = data?.local ?? [];
  const [merging, setMerging] = useState<{ copy: TeamWhereRow; into: TeamWhereRow }>();
  const [mergeError, setMergeError] = useState<string>();
  const [owning, setOwning] = useState<TeamWhereRow>();
  // Check this team: the teams that need attention (stored data only, cheap to ask), the rows whose results are open,
  // and the "Needs attention" filter. `?check=<id>` opens one team's results; `?filter=attention` the filter.
  const attention = useLoad(() => routes.teamAttention().catch(() => undefined), []);
  const needs = new Map((attention.data?.teams ?? []).map((a) => [a.teamId, a]));
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState<Set<string>>(() => new Set(params.get('check') ? [params.get('check')!] : []));
  const onlyAttention = params.get('filter') === 'attention' && needs.size > 0;
  const setFilter = (on: boolean) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (on) n.set('filter', 'attention');
    else n.delete('filter');
    return n;
  }, { replace: true });
  const checkOpen = (id: string, on: boolean) => setOpen((o) => {
    const n = new Set(o);
    if (on) n.add(id);
    else n.delete(id);
    return n;
  });
  const reloadAll = () => {
    where.reload();
    attention.reload();
  };
  const totals = listExtra?.totals;
  const onMerge = data && 'mayMerge' in data && data.mayMerge ? (copy: TeamWhereRow, into: TeamWhereRow) => setMerging({ copy, into }) : undefined;
  const onOwner = data && 'mayMerge' in data && data.mayMerge ? (t: TeamWhereRow) => setOwning(t) : undefined;
  const mayRefresh = !!(data && 'mayRefresh' in data && data.mayRefresh);
  // `/teams/:teamId` (a team's name, here or on a machine's page): that team on its own page.
  // The team being edited, by id: another team's page opens unedited.
  const [editingId, setEditingId] = useState<string>();
  const one = teamId ? teams.find((t) => t.id === teamId) : undefined;
  const mayEdit = !!(data && 'mayMerge' in data && data.mayMerge);
  /** One kind's groups: one per place the workbench keeps them (D286), in the workbench's order; each a row you press. */
  const kindSection = (kind: 'team' | 'specialist') => {
    const rows = teams.filter((t) => (t.kind ?? 'team') === kind && (!onlyAttention || needs.has(t.id)));
    if (onlyAttention && !rows.length) return null;
    const groups = SCOPE_GROUPS.map((g) => ({ ...g, rows: rows.filter((t) => scopeOf(t) === g.id) })).filter((g) => g.rows.length);
    const noun = kind === 'specialist' ? 'specialists' : 'teams';
    const Noun = kind === 'specialist' ? 'Specialists' : 'Teams';
    if (!groups.length) {
      return <ListGroup key={kind} title={Noun} empty={<Empty tone={tone} icon={kind === 'specialist' ? <Person /> : <Persons />} compact title={`No ${noun} yet`} body={`When a workbench shares one of its ${noun} with your organisation, it shows here.`} />} />;
    }
    return groups.map((g) => (
      <ListGroup key={`${kind}/${g.id}`} title={`${Noun} ${g.title.charAt(0).toLowerCase()}${g.title.slice(1)}`} count={g.rows.length}>
        {g.rows.map((t) => (
          <TeamListRow key={t.id} t={t} all={teams} me={me} attention={needs.get(t.id)} />
        ))}
      </ListGroup>
    ));
  };
  const mergeNow = async () => {
    if (!merging) return;
    setMergeError(undefined);
    try {
      await routes.mergeTeam(merging.copy.id, merging.into.id);
      setMerging(undefined);
      reloadAll();
    } catch (e) {
      setMerging(undefined);
      setMergeError(e instanceof Error ? e.message : String(e));
    }
  };
  const copyUsers = merging ? usedBy(merging.copy) : [];
  // `/teams/:teamId` and `/teams/:teamId/members/:member`: the team (or the agent) on its own page, with its Overview
  // for everyone who may read the team and, for those who see where teams run, its Settings and Details.
  if (teamId) {
    const details = one && !forbidden ? (
      <div className="grid gap-4">
        <ErrorNote error={where.error ?? mergeError} />
        <TeamView t={one} all={teams} local={local} me={me} tone={tone} mayEdit={mayEdit} onChanged={reloadAll} onMerge={onMerge} onOwner={onOwner} mayRefresh={mayRefresh} attention={needs.get(one.id)} checking={open.has(one.id)} onCheck={(on) => checkOpen(one.id, on)} editing={editingId === one.id} onEditing={(on) => setEditingId(on ? one.id : undefined)} />
      </div>
    ) : undefined;
    return (
      <Page>
        <TeamScreen teamId={teamId} {...(member ? { member } : {})} {...(one ? { row: one } : {})} tone={tone} details={details} mayEdit={mayEdit} onChanged={reloadAll} />
        {owning ? <OwnerDialog team={owning} onClose={() => setOwning(undefined)} onChanged={where.reload} /> : null}
        {merging ? (
          <ConfirmDialog isOpen title={`Merge this copy into ${merging.into.name}?`} confirmLabel="Merge and remove the copy" danger onCancel={() => setMerging(undefined)} onConfirm={mergeNow}>
            <p>
              {copyUsers.length ? `${copyUsers.join(', ')} will use ${merging.into.name} instead of the copy.` : `No project uses the copy.`} Teams that name the copy as a specialist will name {merging.into.name}.
            </p>
            <p>Then the copy is removed from your organisation. {merging.into.name} keeps its own members, settings and history; whatever differs in the copy is not carried over.</p>
          </ConfirmDialog>
        ) : null}
      </Page>
    );
  }
  return (
    <Page>
      <PageHeader tone={tone} icon={<Persons />} title="Teams" subtitle="Every team and specialist, where each is kept, who uses it and which machines run it." actions={<Button size="sm" variant="secondary" onPress={reloadAll}>Refresh</Button>} />
      <PartBoundary key="teams" name="Teams" tone={tone}>
        {forbidden ? (
          <div className="grid gap-4">
            <YourTeamsNeedingAttention rows={attention.data?.teams ?? []} open={open} onCheck={checkOpen} onChanged={attention.reload} />
            <Empty tone={tone} icon={<Persons />} title="Portfolio managers see the rest" body="Where each team runs is shown to portfolio managers and business-area managers. Your projects' teams are on each project's page, where you can check them too." />
          </div>
        ) : (
          <>
            <ErrorNote error={where.error ?? mergeError} />
            <div className="art-stats">
              <Stat tone={tone} icon={<Persons />} label="Teams" value={totals?.teams} detail="on the platform" />
              <Stat tone={tone} icon={<Persons />} label="Specialists" value={totals?.specialists} detail="on the platform" />
              <Stat tone={tone} icon={<Briefcase />} label="Machines" value={totals?.machines} detail="workbenches and nodes" />
              <Stat tone={tone} icon={<Folder />} label="Local only" value={totals?.local} detail="not on the platform" />
              {needs.size ? (
                <Stat tone={tone} icon={<CircleExclamation />} label="Needs attention" value={needs.size} detail={onlyAttention ? 'showing only these' : 'press to show only these'} needsYou onPress={() => setFilter(!onlyAttention)} selected={onlyAttention} />
              ) : null}
            </div>
            {data ? (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <SearchBox label="Search teams and specialists" value={search.typed} onChange={search.setTyped} placeholder="Search teams and specialists" className="w-72 max-w-full" />
                  {list.loaded ? <span className="text-sm text-muted">{listCount(teams.length, { one: 'team or specialist', many: 'teams and specialists' }, { total: search.q ? undefined : totals ? totals.teams + totals.specialists : undefined, hasMore: list.hasMore, searched: search.q !== '' })}</span> : null}
                </div>
                {search.q && teams.length === 0 && local.length === 0 ? <NoMatch tone={tone} icon={<Persons />} q={search.q} noun="teams or specialists" onClear={search.clear} /> : null}
                {kindSection('team')}
                {kindSection('specialist')}
                {list.hasMore ? <ShowMore onPress={list.showMore} pending={list.loadingMore} /> : null}
                {onlyAttention ? null : (
                  <ListGroup
                    title="Local to one machine"
                    count={local.length || undefined}
                    description="Teams and specialists a machine holds that are not on the platform."
                    empty={local.length ? undefined : <Empty tone={tone} icon={<Persons />} compact title="Nothing local to one machine" body="Every team a machine holds is on the platform." />}
                  >
                    {local.map((l) => {
                      const hit = l.sameName.map((id) => teams.find((t) => t.id === id)).filter((t): t is TeamWhereRow => !!t);
                      return (
                        <ListRow
                          key={`${l.machine.deviceId}:${l.id}`}
                          icon={l.kind === 'specialist' ? <Person /> : <Persons />}
                          title={l.name}
                          description={
                            <>
                              <span className="block">{[l.kind === 'specialist' ? 'Specialist' : 'Team', l.kept ? `kept on ${machineLabel(l.machine)} by choice` : 'not shared yet', memberWords(l.members)].filter(Boolean).join(' · ')}</span>
                              {hit.map((t) => (
                                <span key={t.id} className="block">
                                  A platform team has this name: {t.name} ({scopeWords(t)})
                                </span>
                              ))}
                            </>
                          }
                          value={<MachineLink m={l.machine} me={me} />}
                        />
                      );
                    })}
                  </ListGroup>
                )}
              </>
            ) : null}
          </>
        )}
      </PartBoundary>
      {owning ? <OwnerDialog team={owning} onClose={() => setOwning(undefined)} onChanged={where.reload} /> : null}
      {merging ? (
        <ConfirmDialog isOpen title={`Merge this copy into ${merging.into.name}?`} confirmLabel="Merge and remove the copy" danger onCancel={() => setMerging(undefined)} onConfirm={mergeNow}>
          <p>
            {copyUsers.length ? `${copyUsers.join(', ')} will use ${merging.into.name} instead of the copy.` : `No project uses the copy.`} Teams that name the copy as a specialist will name {merging.into.name}.
          </p>
          <p>Then the copy is removed from your organisation. {merging.into.name} keeps its own members, settings and history; whatever differs in the copy is not carried over.</p>
        </ConfirmDialog>
      ) : null}
    </Page>
  );
}
